// THE ENTITY NAMES OF THE ONE STORE. `/v1/streams` keys every row by
// `(topic, key)`, and for every topic below the topic IS an entity type and the
// key IS that entity's id (`streamTopicsFor`, `apps/api/src/routes/streams.ts`,
// and its entity topics: `key` = the task / ci job / notification / session /
// approval / runner / blocker / plan id). So the account stream store is already
// keyed by entity type + id; this table names the bijection once, so a live read
// says "task tsk_1" and never spells a topic.
//
// TWO TOPICS CAN SERVE ONE ENTITY. `rows` is the topic whose rows the store
// holds — a BOUNDED head (the newest `STREAM_SNAPSHOT_LIMIT` rows), so by
// itself it vouches only for the ids it holds. `everyRow`, where one exists, is a
// SIGNAL-ONLY topic over the entity's newest `updated_at` (`task_changes`: a
// 100-row window, `{id, status, updated_at}`), so a task whose `updated_at` moves
// jumps into it as an upsert, inside the `tasks` head or not, and while it is
// served a by-id read of any id is treated as live. Its `deleted` means "left the
// window", not a change (`account-stream-frames.ts` drops it).
//
// WHAT IT DOES NOT HEAR: a write that changes a task WITHOUT bumping its
// `updated_at`, by design — the queue judge's rank (`keepRank`,
// `clearQueueRanks`), a repo rename's `moveLiveTasks`, the user merge's
// `owning_user_id` repoint (`readTasksChangeToken`'s blind spots,
// `packages/db/src/queries/tasks/reads.ts`) — nor a run's pulse, which comes off
// run events. A read that shows those carries a `refreshMs` backstop
// (`lib/tasks.ts`, `TASK_REFRESH_MS`); "live" here never means "complete". It is optional on
// purpose: a server that does not register it answers it in `ready.ignored`, and
// the read falls back to the head's rule instead of every task read polling.
//
// NOT HERE: `fleet_live`, whose keys are `item:<id>` plus four synthetic frames —
// a projection with its own fold (`fleet-live-frames.ts`), not an entity — and
// the `tasks` topic's `QUEUE_STANDING_KEY` row, which is not a task; a read over
// "every task" hears it as a change (the depth moved), a by-id read never
// matches it.
//
// A new entity is one line here once the api serves its topic, plus its row
// shape in `account-stream-frames.ts` (or a signal-only entry there). A topic
// the server does not register lands in `ready.ignored`, so a read naming it as
// `rows` reads `error` (`statusForTopics`) and keeps polling — never a green
// pill over nothing.

type EntityTopics = { readonly rows: string; readonly everyRow?: string };

export const LIVE_ENTITIES = {
  task: { rows: 'tasks', everyRow: 'task_changes' },
  ci_run: { rows: 'ci' },
  notification: { rows: 'notifications' },
  session: { rows: 'sessions' },
  approval: { rows: 'approvals' },
  machine: { rows: 'machines' },
  blocker: { rows: 'blockers' },
  plan: { rows: 'plans' },
} as const satisfies Record<string, EntityTopics>;

export type LiveEntity = keyof typeof LIVE_ENTITIES;

/** One read's interest: every entity of a type, or one by id. */
export type LiveInterest = {
  readonly entity: LiveEntity;
  /** Absent: any row of this type moving is a change for this read. */
  readonly id?: string;
};

const topicsOf = (entity: LiveEntity): EntityTopics => LIVE_ENTITIES[entity];

/**
 * Wire topic → entity, over BOTH topics of an entity. A `Map`, because the topic
 * arrives off the wire and an object literal answers `constructor`
 * (`account-stream-frames.ts` says how that went once).
 */
const TOPIC_ENTITY: ReadonlyMap<string, LiveEntity> = new Map(
  (Object.keys(LIVE_ENTITIES) as LiveEntity[]).flatMap((entity) => {
    const { rows, everyRow } = topicsOf(entity);
    return everyRow === undefined
      ? [[rows, entity] as const]
      : [[rows, entity] as const, [everyRow, entity] as const];
  }),
);

/** The entity a wire topic's rows belong to, or `undefined` for a non-entity topic. */
export function entityOfTopic(topic: string): LiveEntity | undefined {
  return TOPIC_ENTITY.get(topic);
}

/** The row topics these interests need — the ones a read's LIVENESS is graded on. */
export function rowTopicsFor(interests: readonly LiveInterest[]): string[] {
  return [...new Set(interests.map((i) => topicsOf(i.entity).rows))].sort();
}

/** Every topic to subscribe for these interests: the row topics plus each `everyRow`. */
export function topicsFor(interests: readonly LiveInterest[]): string[] {
  const all = interests.flatMap((i) => {
    const { rows, everyRow } = topicsOf(i.entity);
    return everyRow === undefined ? [rows] : [rows, everyRow];
  });
  return [...new Set(all)].sort();
}

/** The store slice holding this entity's rows. */
export function rowTopicOf(entity: LiveEntity): string {
  return topicsOf(entity).rows;
}

/** The signal-only topic covering every row of this entity, if it has one. */
export function everyRowTopicOf(entity: LiveEntity): string | undefined {
  return topicsOf(entity).everyRow;
}

/** Whether a row moving under `(topic, key)` is a change to something `interests` holds. */
export function interestedIn(
  interests: readonly LiveInterest[],
  topic: string,
  key: string,
): boolean {
  const entity = TOPIC_ENTITY.get(topic);
  if (entity === undefined) return false;
  return interests.some((i) => i.entity === entity && (i.id === undefined || i.id === key));
}
